import { ErrorScreen } from "@/components/ErrorScreen";
import { Logo } from "@/components/layout/logo";
import { ProjectHeader } from "@/components/project/ProjectHeader";
import { ProjectTabContent } from "@/components/project/ProjectTabContent";
import { ProjectTabs } from "@/components/project/ProjectTabs";
import { FpSidebarTrigger } from "@/components/ui/fp-sidebar";
import { getMcpServersQueryOptions } from "@/lib/hooks/useMcpServers";
import { getProjectQueryOptions } from "@/lib/hooks/useProjects";
import { getSecretsQueryOptions } from "@/lib/hooks/useSecrets";
import { useMcpSessionStore } from "@/stores/mcpSessionStore";
import { captureException } from "@sentry/react";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { z } from "zod";

// Search schema for tab navigation
const searchSchema = z.object({
  tab: z
    .enum(["overview", "inspector", "observability", "debug-chat", "deploys"])
    .default("overview"),
  selectedTool: z.string().optional(),
  selectedPrompt: z.string().optional(),
  selectedResource: z.string().optional(),
  params: z.string().optional(), // Base64 encoded tool parameters
  promptParams: z.string().optional(),
  inspectorTab: z.enum(["tools", "prompts", "resources"]).optional(),
  deploymentId: z.string().optional(),
  expandedTrace: z.string().optional(), // For navigating to specific trace in observability tab
});

export type ProjectSearchParams = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/(projects)/projects/$projectId")({
  component: ProjectDetailPage,
  validateSearch: searchSchema,
  loader: async ({ context: { queryClient }, params: { projectId } }) => {
    // Prefetch project data and related information
    await Promise.all([
      queryClient.ensureQueryData(getProjectQueryOptions(projectId)),
      queryClient.ensureQueryData(getMcpServersQueryOptions(projectId)),
      queryClient.ensureQueryData(getSecretsQueryOptions(projectId)),
    ]);
  },
  errorComponent: ({ error }) => {
    useEffect(() => {
      captureException(error);
    }, [error]);

    return (
      <ErrorScreen
        error={error}
        title="Error loading Project"
        message="Failed to load the project details. Please check if the project exists and try again."
      />
    );
  },
});

function ProjectDetailPage() {
  const { projectId } = Route.useParams();
  const { tab } = Route.useSearch();
  const session = useMcpSessionStore((state) => state.getSession(projectId));

  return (
    <div className="w-full h-full">
      <header className="border-b shrink-0">
        <div className="flex items-center justify-between h-12 px-4 min-h-12">
          <div className="flex items-center gap-2">
            <Logo />
            <FpSidebarTrigger />
          </div>
        </div>
      </header>

      <div className="min-h-screen bg-background">
        {/* Project Header - Always visible */}
        <div className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-40">
          <div className="container mx-auto px-4 py-4">
            <ProjectHeader projectId={projectId} session={session} />
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b bg-background">
          <div className="container mx-auto px-4">
            <ProjectTabs projectId={projectId} activeTab={tab} />
          </div>
        </div>

        {/* Tab Content */}
        <div className="container mx-auto px-4 py-6">
          <ProjectTabContent projectId={projectId} activeTab={tab} />
        </div>
      </div>
    </div>
  );
}
